import { useMemo } from "react";
import { useSudoziQuery } from "@src/infrastructure/useSudoziQuery";
import {
  CreateIntakeSubmissionSchema,
  IntakeQuestionType,
} from "@src/sudozi-schemas/intake-submission-schemas";
import { match } from "ts-pattern";
import { IntakeQuestionSchemaType } from "@src/sudozi-schemas/intake-steps-schema";
import { IntakeAnswerType } from "@src/sudozi-schemas/get-intake-submission-schema";
import { v4 } from "uuid";
import {
  GetCustomFormFieldsRoute,
  GetServiceCustomFieldsRoute,
  GetVendorCustomFieldsRoute,
} from "@src/sudozi-schemas/api-route-schemas";
import { QuestionIdToQuestionConfigurationMap } from "@src/pages/intake-requests/new/useNewIntakeRequest";

export function createCustomQuestionIdToConfigurationMapWithServicePeriods({
  steps,
  existingMap,
}: {
  steps: CreateIntakeSubmissionSchema["steps"];
  existingMap: QuestionIdToQuestionConfigurationMap;
}): QuestionIdToQuestionConfigurationMap {
  const customQuestionsThatBelongToServicePeriods = steps
    .slice(2)
    .flatMap((step) => step.fields);

  return steps.reduce<QuestionIdToQuestionConfigurationMap>(
    (map, step, stepIndex) => {
      step.fields.forEach((field) => {
        // prefix the question ID with the service period index if it's a service period step
        const key = encodeQuestionIdKey(field.questionId, stepIndex);
        const config = existingMap[field.questionId];
        if (!config) {
          return;
        }
        // if any of its conditions reference a custom question belonging to a service period,
        // we must also prefix the question ID with the service period index
        map[key] = {
          ...config,
          conditionBooleans: config.conditionBooleans.map((condition) => ({
            ...condition,
            valuesQuestionId: customQuestionsThatBelongToServicePeriods.some(
              (customQuestion) =>
                customQuestion.questionId === condition.valuesQuestionId,
            )
              ? encodeQuestionIdKey(condition.valuesQuestionId, stepIndex)
              : condition.valuesQuestionId,
          })),
          conditionNumerics: config.conditionNumerics.map((condition) => ({
            ...condition,
            valuesQuestionId: customQuestionsThatBelongToServicePeriods.some(
              (customQuestion) =>
                customQuestion.questionId === condition.valuesQuestionId,
            )
              ? encodeQuestionIdKey(condition.valuesQuestionId, stepIndex)
              : condition.valuesQuestionId,
          })),
          conditionAnyOfs: config.conditionAnyOfs.map((condition) => ({
            ...condition,
            valuesQuestionId: customQuestionsThatBelongToServicePeriods.some(
              (customQuestion) =>
                customQuestion.questionId === condition.valuesQuestionId,
            )
              ? encodeQuestionIdKey(condition.valuesQuestionId, stepIndex)
              : condition.valuesQuestionId,
          })),
        };
      });
      return map;
    },
    {},
  );
}

/**
 * Returns a unique ID for a question.
 * If a question belongs to a service item, the ID itself is not unique since there are N service items.
 * In this case, the ID is prefixed with the service period index.
 */
export function encodeQuestionIdKey(questionId: string, stepIndex: number) {
  if (stepIndex < 2) {
    return questionId;
  }
  return `service-period-${stepIndex - 2}::${questionId}`;
}

export function createCustomFieldsFromSchema(
  customFields: IntakeQuestionSchemaType[],
  answers?: IntakeAnswerType[],
) {
  return customFields.map((intakeQuestion) => {
    const answer = answers?.find(
      (customFieldAnswer) => customFieldAnswer.questionId === intakeQuestion.id,
    );
    return {
      description: intakeQuestion.description,
      isRequired: intakeQuestion.isRequired,
      label: intakeQuestion.label,
      questionId: intakeQuestion.id,
      questionOrdinal: intakeQuestion.ordinal,
      stepId: intakeQuestion.step.id,
      version: 1,
      note: null,
      ...match(intakeQuestion)
        .with({ type: "TEXT" }, () => {
          const value =
            answer?.intakeQuestionType === "TEXT" ? answer.value : null;
          return {
            value,
            type: IntakeQuestionType.TEXT as const,
          };
        })

        .with({ type: "REQUEST_TITLE" }, () => {
          const value =
            answer?.intakeQuestionType === "TEXT" ? answer.value : null;
          return {
            value,
            type: IntakeQuestionType.REQUEST_TITLE as const,
          };
        })

        .with({ type: "EMAIL" }, () => {
          const value =
            answer?.intakeQuestionType === "EMAIL" ? answer.value : null;
          return {
            value,
            type: IntakeQuestionType.EMAIL as const,
          };
        })

        .with({ type: "YES_NO" }, () => {
          const value =
            answer?.intakeQuestionType === "YES_NO" ? answer.value : null;
          return {
            value,
            type: IntakeQuestionType.YES_NO as const,
          };
        })

        .with(
          { type: "DATE" },
          { type: "START_DATE" },
          { type: "END_DATE" },
          { type: "DUE_DATE" },
          () => {
            const value =
              answer?.intakeQuestionType === "DATE" ||
              answer?.intakeQuestionType === "START_DATE" ||
              answer?.intakeQuestionType === "END_DATE" ||
              answer?.intakeQuestionType === "DUE_DATE"
                ? answer.value
                : null;
            return {
              value,
              type: IntakeQuestionType.DATE as const,
            };
          },
        )

        .with({ type: "VENDOR_SELECT" }, (rest) => {
          const draftValue =
            answer?.intakeQuestionType === "VENDOR_SELECT"
              ? answer.value
              : null;
          return rest.isMulti
            ? {
                value: draftValue?.map((v) => v.id) || [],
                type: IntakeQuestionType.VENDOR_SELECT as const,
                isMulti: true as const,
              }
            : {
                value: draftValue?.[0]?.id || null,
                type: IntakeQuestionType.VENDOR_SELECT as const,
                isMulti: false as const,
              };
        })

        .with({ type: "SERVICE_SELECT" }, (rest) => {
          const draftValue =
            answer?.intakeQuestionType === "SERVICE_SELECT"
              ? answer.value
              : null;
          return rest.isMulti
            ? {
                value: draftValue?.map((v) => v.id) || [],
                type: IntakeQuestionType.SERVICE_SELECT as const,
                isMulti: true as const,
              }
            : {
                value: draftValue?.[0]?.id || null,
                type: IntakeQuestionType.SERVICE_SELECT as const,
                isMulti: false as const,
              };
        })

        .with({ type: "DEPARTMENT_SELECT" }, (rest) => {
          const draftValue =
            answer?.intakeQuestionType === "DEPARTMENT_SELECT"
              ? answer.value
              : null;
          return rest.isMulti
            ? {
                value: draftValue?.map((v) => v.id) || [],
                type: IntakeQuestionType.DEPARTMENT_SELECT as const,
                isMulti: true as const,
              }
            : {
                value: draftValue?.[0]?.id || null,
                type: IntakeQuestionType.DEPARTMENT_SELECT as const,
                isMulti: false as const,
              };
        })

        .with({ type: "SPEND_CATEGORY_SELECT" }, (rest) => {
          const draftValue =
            answer?.intakeQuestionType === "SPEND_CATEGORY_SELECT"
              ? answer.value
              : null;
          return rest.isMulti
            ? {
                value: draftValue?.map((v) => v.id) || [],
                type: IntakeQuestionType.SPEND_CATEGORY_SELECT as const,
                isMulti: true as const,
              }
            : {
                value: draftValue?.[0]?.id || null,
                type: IntakeQuestionType.SPEND_CATEGORY_SELECT as const,
                isMulti: false as const,
              };
        })

        .with({ type: "BUSINESS_UNIT_SELECT" }, (rest) => {
          const draftValue =
            answer?.intakeQuestionType === "BUSINESS_UNIT_SELECT"
              ? answer.value
              : null;
          return rest.isMulti
            ? {
                value: draftValue?.map((v) => v.id) || [],
                type: IntakeQuestionType.BUSINESS_UNIT_SELECT as const,
                isMulti: true as const,
              }
            : {
                value: draftValue?.[0]?.id || null,
                type: IntakeQuestionType.BUSINESS_UNIT_SELECT as const,
                isMulti: false as const,
              };
        })

        .with({ type: "MEMBER_SELECT" }, (rest) => {
          const draftValue =
            answer?.intakeQuestionType === "MEMBER_SELECT"
              ? answer.value
              : null;
          return rest.isMulti
            ? {
                value: draftValue?.map((v) => v.id) || [],
                type: IntakeQuestionType.MEMBER_SELECT as const,
                isMulti: true as const,
              }
            : {
                value: draftValue?.[0]?.id || null,
                type: IntakeQuestionType.MEMBER_SELECT as const,
                isMulti: false as const,
              };
        })

        .with({ type: "LOCATION_SELECT" }, (rest) => {
          const draftValue =
            answer?.intakeQuestionType === "LOCATION_SELECT"
              ? answer.value
              : null;
          return rest.isMulti
            ? {
                value: draftValue?.map((v) => v.id) || [],
                type: IntakeQuestionType.LOCATION_SELECT as const,
                isMulti: true as const,
              }
            : {
                value: draftValue?.[0]?.id || null,
                type: IntakeQuestionType.LOCATION_SELECT as const,
                isMulti: false as const,
              };
        })

        .with({ type: "ENTITY_SELECT" }, (rest) => {
          const draftValue =
            answer?.intakeQuestionType === "ENTITY_SELECT"
              ? answer.value
              : null;
          return rest.isMulti
            ? {
                value: draftValue?.map((v) => v.id) || [],
                type: IntakeQuestionType.ENTITY_SELECT as const,
                isMulti: true as const,
              }
            : {
                value: draftValue?.[0]?.id || null,
                type: IntakeQuestionType.ENTITY_SELECT as const,
                isMulti: false as const,
              };
        })

        .with({ type: "CUSTOM_SELECT" }, (rest) => {
          const draftValue =
            answer?.intakeQuestionType === "CUSTOM_SELECT"
              ? answer.value
              : null;
          return rest.isMulti
            ? {
                value: draftValue?.map((v) => v.id) || [],
                type: IntakeQuestionType.CUSTOM_SELECT as const,
                isMulti: true as const,
              }
            : {
                value: draftValue?.[0]?.id || null,
                type: IntakeQuestionType.CUSTOM_SELECT as const,
                isMulti: false as const,
              };
        })

        .with({ type: "DOCUMENTS" }, () => {
          const draftValue =
            answer?.intakeQuestionType === "DOCUMENTS" ? answer.value : null;
          return {
            value: draftValue?.map((v) => v.id) || [],
            type: IntakeQuestionType.DOCUMENTS as const,
          };
        })

        .with({ type: "MONEY" }, () => {
          const value =
            answer?.intakeQuestionType === "MONEY" ? answer.value : null;
          return {
            value: value
              ? {
                  value: value.value,
                  currency: value.currency ?? "USD",
                }
              : null,
            type: IntakeQuestionType.MONEY as const,
          };
        })
        .with({ type: "REQUEST_AMOUNT" }, () => {
          const value =
            answer?.intakeQuestionType === "REQUEST_AMOUNT"
              ? answer.value
              : null;
          return {
            value: value
              ? {
                  value: value.value,
                  currency: value.currency ?? "USD",
                }
              : null,
            type: IntakeQuestionType.REQUEST_AMOUNT as const,
          };
        })

        .with({ type: "NUMBER" }, () => {
          const value =
            answer?.intakeQuestionType === "NUMBER" ? answer.value : null;
          return {
            value,
            type: IntakeQuestionType.NUMBER as const,
          };
        })

        .with({ type: "DURATION" }, () => {
          const value =
            answer?.intakeQuestionType === "DURATION"
              ? answer.value
              : {
                  years: null,
                  months: null,
                  days: null,
                };
          return {
            value,
            type: IntakeQuestionType.DURATION as const,
          };
        })

        .with({ type: "ADDRESS" }, () => {
          const value =
            answer?.intakeQuestionType === "ADDRESS" && answer.value
              ? answer.value
              : {
                  address1: "",
                  address2: "",
                  city: "",
                  country: "",
                  postalCode: "",
                  state: "",
                };
          return {
            value,
            type: IntakeQuestionType.ADDRESS as const,
          };
        })

        .with({ type: "NEEDS_REQUEST_SIGNER" }, () => {
          const value =
            answer?.intakeQuestionType === "NEEDS_REQUEST_SIGNER" &&
            answer.value
              ? answer.value
              : false;
          return {
            value,
            type: IntakeQuestionType.NEEDS_REQUEST_SIGNER as const,
          };
        })

        .with({ type: "REQUEST_OWNER_SELECT" }, () => {
          const value =
            answer?.intakeQuestionType === "REQUEST_OWNER_SELECT"
              ? answer.value?.id ?? null
              : null;
          return {
            value: value,
            type: IntakeQuestionType.REQUEST_OWNER_SELECT as const,
            isMulti: false as const,
          };
        })

        .with({ type: "DISPLAY_ONLY" }, () => ({
          type: IntakeQuestionType.DISPLAY_ONLY as const,
          value: null,
        }))

        .exhaustive(),
    };
  });
}

export const useCustomVendorForm = ({
  vendorId,
  enabled = true,
  serviceId,
}: { vendorId?: string; serviceId?: string; enabled?: boolean } = {}) => {
  const { data: customFormFields } = useSudoziQuery({
    route: GetCustomFormFieldsRoute,
    queryKey: ["GET /api/custom-form-fields"],
    options: { enabled },
  });

  const { data: vendorCustomFieldAnswers } = useSudoziQuery({
    route: GetVendorCustomFieldsRoute,
    queryKey: ["GET /api/vendors/:vendorId/custom-fields", vendorId],
    params: { vendorId: vendorId || "" },
    options: { enabled: !!vendorId && enabled },
  });

  const { data: serviceCustomFieldAnswers } = useSudoziQuery({
    route: GetServiceCustomFieldsRoute,
    queryKey: [GetServiceCustomFieldsRoute.path, serviceId],
    params: { serviceId: serviceId || "" },
    options: { enabled: !!serviceId && enabled },
  });

  const createBlankServicePeriodCustomFields = () => {
    return {
      stepId: v4(),
      stepLabel: "Service item fields",
      stepOrdinal: 2,
      fields: customFormFields
        ? createCustomFieldsFromSchema(customFormFields.serviceItemFormFields)
        : [],
    };
  };

  const defaultValues = useMemo<
    CreateIntakeSubmissionSchema | undefined
  >(() => {
    if (!customFormFields) {
      return undefined;
    }

    if (vendorId && !vendorCustomFieldAnswers) {
      return undefined;
    }
    if (serviceId && !serviceCustomFieldAnswers) {
      return undefined;
    }

    const customForm = {
      id: "",
      intakeCategoryId: "",
      isDraft: false,
      watcherUserIds: [],

      steps: [
        {
          stepId: v4(),
          stepLabel: "Vendor fields",
          stepOrdinal: 0,
          fields: createCustomFieldsFromSchema(
            customFormFields.vendorFormFields,
            vendorCustomFieldAnswers?.map((v) => v.answer).filter(Boolean) as
              | IntakeAnswerType[]
              | undefined,
          ),
        },
        {
          stepId: v4(),
          stepLabel: "Service fields",
          stepOrdinal: 1,
          fields: createCustomFieldsFromSchema(
            customFormFields.serviceFormFields,
            serviceCustomFieldAnswers?.serviceCustomFields
              .map((v) => v.answer)
              .filter(Boolean) as IntakeAnswerType[] | undefined,
          ),
        },
      ],
    };

    if (serviceId && serviceCustomFieldAnswers) {
      for (const [servicePeriodId, customFieldAnswers] of Object.entries(
        serviceCustomFieldAnswers.servicePeriodCustomFields,
      )) {
        customForm.steps.push({
          stepId: servicePeriodId,
          stepLabel: "Service item fields",
          stepOrdinal: 0,
          fields: createCustomFieldsFromSchema(
            customFormFields.serviceItemFormFields,
            customFieldAnswers.map((v) => v.answer).filter(Boolean) as
              | IntakeAnswerType[]
              | undefined,
          ),
        });
      }
    } else {
      customForm.steps.push({
        stepId: v4(),
        stepLabel: "Service item fields",
        stepOrdinal: 2,
        fields: createCustomFieldsFromSchema(
          customFormFields.serviceItemFormFields,
        ),
      });
    }

    return customForm;
  }, [
    customFormFields,
    vendorId,
    vendorCustomFieldAnswers,
    serviceId,
    serviceCustomFieldAnswers,
  ]);

  const customQuestionIdToConfigurationMap = useMemo(() => {
    if (!customFormFields) {
      return undefined;
    }

    const map: {
      [questionId: string]: NonNullable<
        typeof customFormFields
      >["vendorFormFields"][number];
    } = {};

    customFormFields.vendorFormFields.forEach((question) => {
      map[question.id] = question;
    });
    customFormFields.serviceFormFields.forEach((question) => {
      map[question.id] = question;
    });
    customFormFields.serviceItemFormFields.forEach((question) => {
      map[question.id] = question;
    });

    return map;
  }, [customFormFields]);

  return {
    defaultValues,
    customQuestionIdToConfigurationMap,
    createBlankServicePeriodCustomFields,
  };
};
